import { Component, OnInit, HostListener, ElementRef, ViewChild, AfterContentInit } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { NavRoute } from 'app/shared/navigation/nav.route.interface';
import { SecurityService } from 'app/core/security/security.service';
import { UserJsVm } from 'app/shared/generated/Administration/Models/UserJsVm';
import { NavigationService, NavDividerCharacter } from 'app/shared/navigation/navigation.service';
import { TopClickNavComponent } from 'app/shared/navigation/top-click-nav/top-click-nav.component';
import { pcgSettings } from 'app/shared/generated/pcg-settings';
import { getMainNavRoutes } from '../../main-nav-routes';
import { CheckNetworkService } from 'local-db/services/check-network.service';

@Component({
    selector: 'pcg-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    standalone: false
})
export class TopHeaderComponent implements OnInit, AfterContentInit {

	@ViewChild(TopClickNavComponent, { static: true }) nav: TopClickNavComponent;
	@ViewChild('userNav') userNav: ElementRef;
	@ViewChild('mainHamburger', { static: true }) mainHamburger: ElementRef;
	@ViewChild('btnSearch') btnSearch: ElementRef;

	user: UserJsVm;
	navRoutes: NavRoute[];
	currForceSelect: string;
	currMainNavMenu: string;
	currSelectedNavMenu: NavRoute;
	navDividerCharacter = NavDividerCharacter;
	mode = pcgSettings.mode;

	userNavOpen = false;
	adminAccess = false;
	shouldOpenSearch = false;

	navigationOffset = 0;

	constructor(
		private sec: SecurityService
		, private router: Router
		, private navService: NavigationService
		, private modalService: NgbModal
	) { }

	ngOnInit() {
		this.sec.user$.subscribe(user => { this.user = user; });
		this.navService.navRoutes$.subscribe(navRoutes => { this.navRoutes = this.navService.getFlatMenu(navRoutes).filter(o => !o.parentNav); });
		this.navService.currOpenPrimaryNavMenu$.subscribe(currMainNavMenu => { this.currMainNavMenu = currMainNavMenu; });
		this.navService.currSelectedMainNavMenu$.subscribe(currSelectedNavMenu => {
			this.currSelectedNavMenu = currSelectedNavMenu;
			if (
				!currSelectedNavMenu 
				|| currSelectedNavMenu.id === 'root'
			) { this.navService.setOpenSecondaryMenu([]); }
		});
		this.navService.currOpenSecondaryNavMenu$.subscribe(currSecondaryNavMenu => {
			this.currSelectedNavMenu = currSecondaryNavMenu;
			this.fixNavOffset();
		});
		this.navService.currForceSelect$.subscribe(currForceSelect => { this.currForceSelect = currForceSelect; });

		// Subscribe to route changes and update the navs when they happen
		this.router.events.pipe(filter(event => event instanceof NavigationEnd))
			.subscribe((event: NavigationEnd) => {
				this.fixNavOffset();
				this.navService.navRoutes.next(this.sec.getSecureNavItems(getMainNavRoutes()));
			}
		);
	}

	// Logout removes all of our security variables
	// and redirects user to root
	logout() {
		const isOnline = CheckNetworkService.checkNetworkStatus();
		if (
			isOnline
			|| (
				!isOnline
				&& confirm("Are you sure you want log out? You are offline and may not be able to log in until you are back online.")
			)
		) {
			this.sec.setSecurity(null, null, null);
			this.deleteAllCookies();
			this.router.navigate(['/']);
		}
	}

	// Checks if user can view Admin Dashboard
	checkAccess() { this.adminAccess = this.sec.hasAccess(o => o.manageUsers); }

	/** Handle clicking any link in the user navigation **/
	clickUserLink() { this.userNavOpen = false; }

	// This is used above to delete all cookies on logout
	deleteAllCookies() {
		const cookies = document.cookie.split(';');

		for (let i = 0; i < cookies.length; i++) {
			const cookie = cookies[i];
			const eqPos = cookie.indexOf('=');
			const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
			document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT';
		}
	}

	// Toggle whether or not the main navigation is open
	toggleNav() {
		if (this.currMainNavMenu === '') { this.navService.setCurrOpenNavMenu('root'); }
		else { this.navService.setCurrOpenNavMenu(''); }
	}

	// Close the main navigation when they click outside of it
	// and are not clicking the mobile hamburger button
	@HostListener('document:click', ['$event'])
	clickDoc(event) {
		if (
			!this.mainHamburger?.nativeElement.contains(event.target)
			&& !this.nav?.elRef?.nativeElement.contains(event.target)
			&& !this.userNav?.nativeElement.contains(event.target)
		) {
			this.navService.setCurrOpenNavMenu('');
			this.userNavOpen = false;
		}
	}

	// Used in the keyup function belew to prevent bringing up the search
	// if they are focused in a form field
	isFormField(e) {
		if (!(e instanceof HTMLElement)) { return false; }
		const t = e.nodeName.toLowerCase();
		const n = (e.getAttribute('type') || '').toLowerCase();
		return 'select' === t 
			|| 'textarea' === t 
			|| 'input' === t 
			&& 'submit' !== n 
			&& 'reset' !== n 
			|| e.isContentEditable;
	}

	// Open search when they hit the '/' (or '`') key
	@HostListener('document:keydown', ['$event'])
	onKeydown(event: KeyboardEvent) {
		if (
			(
				event.key === '/' 
				|| event.key === '`'
			) 
			&& !this.isFormField(event.target)
		) { event.preventDefault(); }
	}

	// Fix the nav offset on resize
	@HostListener('window:resize')
	onResize() { this.fixNavOffset(); }

	// Fix the nav offset on content init
	ngAfterContentInit() { this.fixNavOffset(); }

	/** This adds dynamic CSS based on the navigation size.
	 * Specifically, it does the following:
	 * 1. Adds padding to the top of the page so nothing is hidden by the fixed nav
	 * 2. Sets the top offset for our PCG table sticky headers.
	 */
	fixNavOffset() {
		const mainNav = document.getElementById('pcgMainNavigation');
		let secondaryNavOffset = null;
		setTimeout(o => {
			if (getComputedStyle(mainNav, null).position === 'fixed') {
				this.navigationOffset = mainNav.getBoundingClientRect().bottom;
				const secondaryNav = document.getElementById('secondaryNav');
				const bottomArrow = document.querySelector('.bottom-arrow:not(.small-screen-only)');

				if (
					secondaryNav 
					&& bottomArrow
				) { secondaryNavOffset = -1 * Math.ceil(bottomArrow.getBoundingClientRect().top); }
			} else { this.navigationOffset = 0; }

			let styles = `
				.pcg-table-fixed-header thead tr th, .pcg-table-fixed-header thead tr, pcg-side-nav {
					top: ${this.navigationOffset - 1}px
				}` +
				`.nav-padding-placeholder {
					padding-top: ${this.navigationOffset}px;
				}`;

			this.setDynamicStyle('navPaddingAndTableOffset', styles);
			if (
				secondaryNavOffset !== null 
				&& secondaryNavOffset !== 0
			) { this.setDynamicStyle('bottomArrowStyle', `.bottom-arrow:before { bottom: ${secondaryNavOffset}px`); }
			else if (!document.getElementById('bottomArrowStyle')) {
				this.setDynamicStyle('bottomArrowStyle', `.bottom-arrow:before { bottom: -12px`);
			}
		}, 100);
	}

	setDynamicStyle(name: string, styles: string) {
		let dynamicStyle = document.getElementById(name);
		if (dynamicStyle) { document.head.removeChild(dynamicStyle); }
		dynamicStyle = document.createElement('style');
		dynamicStyle.id = name;
		dynamicStyle.innerHTML = styles;
		document.head.appendChild(dynamicStyle);
	}
}
